import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import KineticText from "@/components/motion/KineticText";
import CtaButton from "@/components/shared/CtaButton";
import { STATS } from "@/data/site";

function AINode() {
  const nodes = [
    { cx: 120, cy: 70, r: 5 }, { cx: 250, cy: 40, r: 4 }, { cx: 330, cy: 120, r: 6 },
    { cx: 90, cy: 200, r: 4 }, { cx: 210, cy: 170, r: 7 }, { cx: 320, cy: 250, r: 5 },
    { cx: 160, cy: 300, r: 4 }, { cx: 60, cy: 330, r: 5 }, { cx: 280, cy: 340, r: 4 },
  ];
  const links = [[0, 1], [1, 2], [0, 4], [4, 2], [3, 4], [4, 5], [3, 6], [6, 7], [6, 8], [5, 8], [4, 6]];
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full">
      <defs>
        <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#0B63F6" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#0B63F6" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="200" r="200" fill="url(#nodeGlow)" />
      {links.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={nodes[a].cx} y1={nodes[a].cy} x2={nodes[b].cx} y2={nodes[b].cy}
          stroke="#0B63F6" strokeWidth="1" strokeOpacity="0.25"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.6 + i * 0.05, ease: "easeOut" }}
        />
      ))}
      {nodes.map((n, i) => (
        <motion.circle
          key={i}
          cx={n.cx} cy={n.cy} r={n.r}
          fill={i % 3 === 0 ? "#F36F21" : i % 2 === 0 ? "#6E56CF" : "#0B63F6"}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 1.2, 1], opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 + i * 0.06 }}
          style={{ transformOrigin: `${n.cx}px ${n.cy}px` }}
        />
      ))}
    </svg>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGraphic = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 18]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-white pt-32 pb-20 lg:pt-44 lg:pb-28" data-testid="hero">
      <div className="absolute inset-0 bg-grid opacity-[0.5]" />
      <div
        className="absolute top-0 right-0 h-[600px] w-[600px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(110,86,207,0.10), transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-4 py-1.5 text-xs font-medium text-gray-600"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#19A974]" />
            India&apos;s leading Generative Engine Optimization consultancy
          </motion.div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-[5rem] font-extrabold tracking-tightest leading-[0.98] mt-6 text-[#111111]">
            <KineticText lines={["Be the brand", "AI chooses to"]} />
            <span className="line-mask">
              <motion.span
                style={{ display: "block" }}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 + 2 * 0.12 }}
              >
                <span className="text-[#0B63F6]">recommend.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="text-lg lg:text-xl text-gray-600 mt-7 max-w-2xl leading-relaxed"
          >
            CompuBrain AI improves your visibility across AI-powered search platforms through Generative
            Engine Optimization. We optimize your content, structured data, brand entities and digital
            authority so ChatGPT, Gemini, Claude and Perplexity can discover, understand, trust and
            recommend your business.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <CtaButton variant="accent" source="hero" title="Request AI Visibility Audit" testId="hero-cta-primary">
              Request AI Visibility Audit
            </CtaButton>
            <Link
              to="/tools"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 px-7 py-3.5 text-[15px] font-medium text-[#111111] hover:border-[#111111] transition-all"
            >
              Free AI Visibility Check
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl border-t border-gray-100 pt-8"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl font-extrabold tracking-tightest text-[#111111]">{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 hidden lg:block">
          <motion.div style={{ y: yGraphic, rotate, scale }} className="relative aspect-square max-w-md ml-auto">
            <AINode />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
